import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ScrollToTop } from './components/ScrollToTop'
import { Login } from './pages/Login'
import { AcceptInvite } from './pages/AcceptInvite'
import { SetPassword } from './pages/SetPassword'
import { ShareView } from './pages/ShareView'
import { Dashboard } from './pages/Dashboard'
import { DashboardLayout } from './components/DashboardLayout'
import { ProtectedRoute } from './components/ProtectedRoute'
import { Toaster } from './components/ui/toaster'
import { APP_MODE } from './config/domains'
import './lib/i18n' // Initialize i18n

// In development (localhost), show all routes for easier testing
const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost'
const showWwwRoutes = isDev || APP_MODE === 'www'
const showAppRoutes = isDev || APP_MODE === 'app'

// Lazy load pages
const UnifiedProjectView = lazy(() => import('./components/unified/UnifiedProjectViewV2').then(m => ({ default: m.UnifiedProjectViewV2 })))
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })))
const NotificationSettings = lazy(() => import('./pages/NotificationSettings').then(m => ({ default: m.NotificationSettings })))

// New settings pages
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage').then(m => ({ default: m.ProfilePage })))
const AccountPage = lazy(() => import('./pages/settings/AccountPage').then(m => ({ default: m.AccountPage })))
const PreferencesPage = lazy(() => import('./pages/settings/PreferencesPage').then(m => ({ default: m.PreferencesPage })))

// New admin pages
const AgencyManagementPage = lazy(() => import('./pages/admin/AgencyManagementPage').then(m => ({ default: m.AgencyManagementPage })))
const TeamPage = lazy(() => import('./pages/admin/TeamPage').then(m => ({ default: m.TeamPage })))
const BillingPage = lazy(() => import('./pages/admin/BillingPage').then(m => ({ default: m.BillingPage })))

// Tokens page
const TokensPage = lazy(() => import('./pages/TokensPage').then(m => ({ default: m.TokensPage })))

// Super admin monitoring pages
const SuperAdminDashboardNew = lazy(() => import('./pages/admin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })))
const CostMonitoring = lazy(() => import('./pages/admin/CostMonitoring').then(m => ({ default: m.CostMonitoring })))
const AbuseMonitoring = lazy(() => import('./pages/admin/AbuseMonitoring').then(m => ({ default: m.AbuseMonitoring })))
const GlobalNotificationSettings = lazy(() => import('./pages/admin/GlobalNotificationSettings').then(m => ({ default: m.GlobalNotificationSettings })))
const AgencyNotificationSettings = lazy(() => import('./pages/admin/AgencyNotificationSettings').then(m => ({ default: m.AgencyNotificationSettings })))
const AdminTokenPricing = lazy(() => import('./pages/admin/AdminTokenPricing').then(m => ({ default: m.AdminTokenPricing })))
const AdminTokenAccounts = lazy(() => import('./pages/admin/AdminTokenAccounts').then(m => ({ default: m.AdminTokenAccounts })))
const AIModelSettings = lazy(() => import('./pages/admin/AIModelSettings').then(m => ({ default: m.AIModelSettings })))
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage').then(m => ({ default: m.UserManagementPage })))
const BroadcastsPage = lazy(() => import('./pages/admin/BroadcastsPage').then(m => ({ default: m.BroadcastsPage })))
const KnowledgeManagementPage = lazy(() => import('./pages/admin/KnowledgeManagementPage').then(m => ({ default: m.KnowledgeManagementPage })))
const AnalyticsDebug = lazy(() => import('./pages/admin/AnalyticsDebug').then(m => ({ default: m.AnalyticsDebug })))
const AgentManagementPage = lazy(() => import('./pages/admin/AgentManagementPage').then(m => ({ default: m.AgentManagementPage })))
const AdminApiKeys = lazy(() => import('./pages/admin/AdminApiKeys').then(m => ({ default: m.AdminApiKeys })))
const AccountIntelAdminPage = lazy(() => import('./pages/admin/AccountIntelAdminPage').then(m => ({ default: m.AccountIntelAdminPage })))
const AiSdkSpikePage = lazy(() => import('./pages/admin/AiSdkSpikePage').then(m => ({ default: m.AiSdkSpikePage })))

// Onboarding test page (DEV ONLY — remove before shipping)
const OnboardingTestPage = lazy(() => import('./pages/OnboardingTestPage').then(m => ({ default: m.OnboardingTestPage })))

// Components gallery (DEV ONLY — visual playground for the shells layer)
const ComponentsGalleryPage = lazy(() => import('./pages/dev/ComponentsGalleryPage'))

// Campaign workbench preview (DEV ONLY — shell-based campaign view)
const CampaignWorkbenchPreviewPage = lazy(() => import('./pages/dev/CampaignWorkbenchPreviewPage'))

// Agent run detail preview (DEV ONLY — shell-based agent detail view)
const AgentRunDetailPreviewPage = lazy(() => import('./pages/dev/AgentRunDetailPreviewPage'))

// Insights preview (DEV ONLY — shell-based insights view with timeline + graph)
const InsightsPreviewPage = lazy(() => import('./pages/dev/InsightsPreviewPage'))

// Chat artifacts preview (DEV ONLY — adapter from ChatArtifact to shells)
const ChatArtifactsPreviewPage = lazy(() => import('./pages/dev/ChatArtifactsPreviewPage'))

// Internal Process Documentation (super_admin only)
const ProcessMapPage = lazy(() => import('./pages/ProcessMapPage'))

// OAuth callback pages
const OAuthCallbackGsc = lazy(() => import('./pages/OAuthCallbackGsc').then(m => ({ default: m.OAuthCallbackGsc })))
const OAuthCallbackGa4 = lazy(() => import('./pages/OAuthCallbackGa4').then(m => ({ default: m.OAuthCallbackGa4 })))
const OAuthCallbackGads = lazy(() => import('./pages/OAuthCallbackGads').then(m => ({ default: m.OAuthCallbackGads })))

// Project settings page
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage').then(m => ({ default: m.ProjectSettingsPage })))

// Asset Library page
const AssetLibrary = lazy(() => import('./pages/AssetLibrary').then(m => ({ default: m.AssetLibrary })))

// Onboarding pages
const Connect = lazy(() => import('./pages/Connect').then(m => ({ default: m.Connect })))
const ConnectProgress = lazy(() => import('./components/onboarding/ConnectionProgress').then(m => ({ default: m.ConnectionProgress })))

// Redirect helper for deprecated onboarding route
function OnboardingRedirect() {
  const { projectId } = useParams()
  return <Navigate to={`/project/${projectId}`} replace />
}

// Public pages (www.framed.dev)
const HomePageNew = lazy(() => import('./pages/HomePageNew').then(m => ({ default: m.HomePageNew })))
const TryLanding = lazy(() => import('./pages/TryLanding').then(m => ({ default: m.TryLanding })))
const UpgradeLanding = lazy(() => import('./pages/UpgradeLanding').then(m => ({ default: m.UpgradeLanding })))
const PublicChangelog = lazy(() => import('./pages/PublicChangelog').then(m => ({ default: m.PublicChangelog })))
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

// Trial flow pages (www.framed.dev/try)
const TrialPhase1 = lazy(() => import('./pages/trial/TrialPhase1').then(m => ({ default: m.TrialPhase1 })))
const TrialPhase2 = lazy(() => import('./pages/trial/TrialPhase2').then(m => ({ default: m.TrialPhase2 })))
const TrialPhase3 = lazy(() => import('./pages/trial/TrialPhase3').then(m => ({ default: m.TrialPhase3 })))
const TrialExport = lazy(() => import('./pages/trial/TrialExport').then(m => ({ default: m.TrialExport })))
const TrialTransition = lazy(() => import('./pages/trial/TrialTransition').then(m => ({ default: m.TrialTransition })))

// Trial demo pages (dev-only)
const TrialLayout = lazy(() => import('./pages/trial-demo/TrialLayout'))
const ForMeTrialPage = lazy(() => import('./pages/trial-demo/ForMeTrialPage'))
const InboxTrialPage = lazy(() => import('./pages/trial-demo/InboxTrialPage'))
const ProjectTrialPage = lazy(() => import('./pages/trial-demo/ProjectTrialPage'))
const WidgetDemoTrialPage = lazy(() => import('./pages/trial-demo/WidgetDemoTrialPage'))
const SettingsTrialPage = lazy(() => import('./pages/trial-demo/SettingsTrialPage'))
const AnalyticsTrialPage = lazy(() => import('./pages/trial-demo/AnalyticsTrialPage'))

// Signup pages (app.framed.dev)
const SignupPage = lazy(() => import('./pages/SignupPage').then(m => ({ default: m.SignupPage })))
const SignupSuccessPage = lazy(() => import('./pages/SignupSuccessPage').then(m => ({ default: m.SignupSuccessPage })))

// Public billing pages (www.framed.dev)
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })))
const TokenPricingExplainerPage = lazy(() => import('./pages/TokenPricingExplainerPage').then(m => ({ default: m.TokenPricingExplainerPage })))
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })))
const ForAgenciesPage = lazy(() => import('./pages/ForAgenciesPage').then(m => ({ default: m.ForAgenciesPage })))
const ResourcesPage = lazy(() => import('./pages/ResourcesPage').then(m => ({ default: m.ResourcesPage })))
const ResourceDetailPage = lazy(() => import('./pages/ResourceDetailPage').then(m => ({ default: m.ResourceDetailPage })))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })))
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage').then(m => ({ default: m.CheckoutSuccessPage })))
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage').then(m => ({ default: m.CheckoutCancelPage })))
const ShopifyAppListing = lazy(() => import('./pages/ShopifyAppListing').then(m => ({ default: m.ShopifyAppListing })))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-framed-yellow-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

// Redirect all /wizard/:id/* routes → /project/:id?view=...
const WizardRedirect = ({ view }: { view?: string }) => {
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()
  const newParams = new URLSearchParams(searchParams)
  if (view) newParams.set('view', view)
  const qs = newParams.toString()
  return <Navigate to={`/project/${projectId}${qs ? `?${qs}` : ''}`} replace />
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <Toaster />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ======================= */}
              {/* WWW Routes (Marketing)  */}
              {/* ======================= */}
              {showWwwRoutes && (
                <>
                  {/* Show HomePageNew on root for www mode */}
                  <Route path="/" element={<HomePageNew />} />
                  <Route path="/home" element={<HomePageNew />} />
                  <Route path="/try" element={<TryLanding />} />
                  <Route path="/try/:sessionId" element={<TryLanding />} />
                  {/* Trial flow routes */}
                  <Route path="/try/:sessionId/phase1" element={<TrialPhase1 />} />
                  <Route path="/try/:sessionId/phase2" element={<TrialPhase2 />} />
                  <Route path="/try/:sessionId/phase3" element={<TrialPhase3 />} />
                  <Route path="/try/:sessionId/export" element={<TrialExport />} />
                  <Route path="/try/:sessionId/transition" element={<TrialTransition />} />
                  <Route path="/upgrade" element={<UpgradeLanding />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/pricing/how-it-works" element={<TokenPricingExplainerPage />} />
                  <Route path="/tokens" element={<Navigate to="/pricing/how-it-works" replace />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/resources/:resourceId" element={<ResourceDetailPage />} />
                  <Route path="/for-agencies" element={<ForAgenciesPage />} />
                  <Route path="/for/shopify" element={<ShopifyAppListing />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                  <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
                  {/* Connect / onboarding (accessible without auth) */}
                  <Route path="/connect" element={<Connect />} />
                  <Route path="/connect/progress/:projectId" element={<ConnectProgress />} />
                </>
              )}

              {/* ======================= */}
              {/* APP Routes (Application) */}
              {/* ======================= */}
              {showAppRoutes && (
                <>
                  {/* Root redirect to dashboard (default for app mode and dev) */}
                  {(APP_MODE === 'app' || isDev) && <Route path="/" element={<Navigate to="/dashboard" replace />} />}

                  {/* Auth routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  <Route path="/set-password" element={<SetPassword />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/signup/success" element={<SignupSuccessPage />} />

                  {/* Connect / onboarding (accessible without auth) */}
                  <Route path="/connect" element={<Connect />} />
                  <Route path="/connect/progress/:projectId" element={<ConnectProgress />} />

                  {/* Public share route (accessible without auth) */}
                  <Route path="/share/:token" element={<ShareView />} />

                  {/* Public changelog route (accessible without auth) */}
                  <Route path="/changelog/:projectId" element={<PublicChangelog />} />

                  {/* Public API docs (accessible without auth) */}
                  <Route path="/docs/api" element={<ApiDocsPage />} />

                  {/* Dashboard Layout Routes - Protected with persistent sidebar */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="onboarding-test" element={<OnboardingTestPage />} />

                    {/* DEV ONLY — gated by isDev check; visual playground for shells primitives */}
                    {isDev && <Route path="dev/components" element={<ComponentsGalleryPage />} />}
                    {isDev && <Route path="dev/campaign-workbench" element={<CampaignWorkbenchPreviewPage />} />}
                    {isDev && <Route path="dev/agent-detail" element={<AgentRunDetailPreviewPage />} />}
                    {isDev && <Route path="dev/insights" element={<InsightsPreviewPage />} />}
                    {isDev && <Route path="dev/chat-artifacts" element={<ChatArtifactsPreviewPage />} />}

                    {/* Tokens Page */}
                    <Route path="tokens" element={<TokensPage />} />

                    {/* Internal Process Documentation (super_admin only) */}
                    <Route path="processes" element={<ProcessMapPage />} />

                    {/* Settings Routes */}
                    <Route path="settings">
                      <Route index element={<Navigate to="/settings/profile" replace />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="account" element={<AccountPage />} />
                      <Route path="preferences" element={<PreferencesPage />} />
                    </Route>

                    {/* Admin Routes */}
                    <Route path="admin">
                      <Route index element={<Navigate to="/admin/team" replace />} />
                      <Route path="team" element={<TeamPage />} />
                      <Route path="billing" element={<BillingPage />} />
                      <Route path="agency-management" element={<AgencyManagementPage />} />
                      {/* Super Admin Monitoring Pages */}
                      <Route path="super-admin" element={<SuperAdminDashboardNew />} />
                      <Route path="cost-monitoring" element={<CostMonitoring />} />
                      <Route path="abuse-monitoring" element={<AbuseMonitoring />} />
                      {/* Notification Settings Pages */}
                      <Route path="global-notifications" element={<GlobalNotificationSettings />} />
                      <Route path="agency-notifications" element={<AgencyNotificationSettings />} />
                      {/* Token Management */}
                      <Route path="tokens/pricing" element={<AdminTokenPricing />} />
                      <Route path="tokens/accounts" element={<AdminTokenAccounts />} />
                      {/* AI Model Configuration */}
                      <Route path="ai-models" element={<AIModelSettings />} />
                      {/* User Management */}
                      <Route path="users" element={<UserManagementPage />} />
                      {/* Broadcasts */}
                      <Route path="broadcasts" element={<BroadcastsPage />} />
                      {/* Knowledge Management */}
                      <Route path="knowledge" element={<KnowledgeManagementPage />} />
                      {/* Agent Management — includes Registry tab */}
                      <Route path="agents" element={<AgentManagementPage />} />
                      {/* API Keys */}
                      <Route path="api-keys" element={<AdminApiKeys />} />
                      {/* Analytics Debug */}
                      <Route path="analytics-debug" element={<AnalyticsDebug />} />
                      {/* Account Intelligence test panel + health dashboard */}
                      <Route path="account-intel" element={<AccountIntelAdminPage />} />
                      {/* AI SDK Spike — debug surface for the Vercel AI SDK migration spike */}
                      <Route path="aisdk-spike" element={<AiSdkSpikePage />} />
                    </Route>
                  </Route>

                  {/* Legacy redirects */}
                  <Route path="/super-admin" element={<Navigate to="/admin/agency-management" replace />} />

                  {/* OAuth Callback Routes */}
                  <Route
                    path="/oauth/callback/gsc"
                    element={
                      <ProtectedRoute>
                        <OAuthCallbackGsc />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/oauth/callback/ga4"
                    element={
                      <ProtectedRoute>
                        <OAuthCallbackGa4 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/oauth/callback/gads"
                    element={
                      <ProtectedRoute>
                        <OAuthCallbackGads />
                      </ProtectedRoute>
                    }
                  />

                  {/* Project Settings Page */}
                  <Route
                    path="/project/:projectId/settings"
                    element={
                      <ProtectedRoute>
                        <ProjectSettingsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Asset Library Page */}
                  <Route
                    path="/project/:projectId/assets"
                    element={
                      <ProtectedRoute>
                        <AssetLibrary />
                      </ProtectedRoute>
                    }
                  />

                  {/* Legacy onboarding route — redirect to project view (goal-first modal handles onboarding now) */}
                  <Route
                    path="/project/:projectId/onboarding"
                    element={<OnboardingRedirect />}
                  />

                  {/* Unified Project View - Email-client style workspace */}
                  <Route
                    path="/project/:projectId"
                    element={
                      <ProtectedRoute>
                        <UnifiedProjectView />
                      </ProtectedRoute>
                    }
                  />

                  {/* Wizard Routes — All redirect to unified project view */}
                  <Route path="/wizard/:projectId/phase1" element={<WizardRedirect view="discovery" />} />
                  <Route path="/wizard/:projectId/phase2" element={<WizardRedirect view="project-details" />} />
                  <Route path="/wizard/:projectId/build" element={<WizardRedirect view="build-spec" />} />
                  <Route path="/wizard/:projectId/build-status" element={<WizardRedirect view="builds" />} />
                  <Route path="/wizard/:projectId/overview" element={<WizardRedirect view="project-overview" />} />
                  <Route path="/wizard/:projectId/import" element={<WizardRedirect view="project-overview" />} />
                  <Route path="/wizard/:projectId/phase3" element={<WizardRedirect view="build-spec" />} />
                  <Route path="/wizard/:projectId/build-spec" element={<WizardRedirect view="build-spec" />} />
                  <Route path="/wizard/:projectId/phase4" element={<WizardRedirect />} />
                  <Route path="/wizard/:projectId/phase4/feedback" element={<WizardRedirect />} />
                  <Route path="/wizard/:projectId/phase4-old" element={<WizardRedirect />} />
                  <Route path="/wizard/:projectId/phase5" element={<WizardRedirect />} />

                  {/* Notifications - Keep separate for now */}
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <Notifications />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications/settings"
                    element={
                      <ProtectedRoute>
                        <NotificationSettings />
                      </ProtectedRoute>
                    }
                  />
                </>
              )}

              {/* Dev-only: quick access to homepage at /home */}
              {isDev && APP_MODE !== 'www' && (
                <Route path="/home" element={<HomePageNew />} />
              )}

              {/* Dev-only: trial demo pages */}
              {isDev && (
                <>
                  <Route path="/trial" element={<TrialLayout />}>
                    <Route index element={<Navigate to="/trial/for-me" replace />} />
                    <Route path="for-me" element={<ForMeTrialPage />} />
                    <Route path="inbox" element={<InboxTrialPage />} />
                    <Route path="project" element={<ProjectTrialPage />} />
                    <Route path="analytics" element={<AnalyticsTrialPage />} />
                    <Route path="widget" element={<WidgetDemoTrialPage />} />
                    <Route path="settings" element={<SettingsTrialPage />} />
                  </Route>
                  {/* Legacy redirects */}
                  <Route path="/feed-trial" element={<Navigate to="/trial/for-me" replace />} />
                  <Route path="/trial/feed" element={<Navigate to="/trial/for-me" replace />} />
                  <Route path="/trial/dashboard" element={<Navigate to="/trial/for-me" replace />} />
                </>
              )}

              {/* 404 catch-all route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
